import { type ChangeEvent, useCallback, useState } from 'react';
import BigNumber from 'bignumber.js';
import { type Token } from '@/shared/assets/tokens';
import { decimalRegex } from '@/shared/utils/regex';
import { removeCommas } from '@/shared/utils/strings';
import TokenAmount from '@/shared/utils/TokenAmount';

function validateInputChange(_value: string, token: Token | undefined): string | null {
  const value = removeCommas(_value.replace(/,$/, '.')); // replace trailing comma with period for eu dummies and then remove all commas.
  if (value === '.') {
    return '0.';
  }
  const onlyDecimals = value.match(/^(\.)(\d+)$/); // match a period followed by digits (backspace case) eg: .1234
  if (onlyDecimals) {
    const [, , decimals] = onlyDecimals;
    return `.${decimals}`;
  }

  if (!decimalRegex(token?.decimals).test(value)) return null;

  return value;
}

const formatValue = (
  newValue: BigNumber.Value,
  capped: boolean | undefined,
  token: Token | undefined,
): string => {
  if (newValue === '') {
    return '';
  }

  let cleaned = newValue;
  if (typeof cleaned === 'string') {
    cleaned = removeCommas(cleaned);
  }

  const nearestValidAmount = token
    ? BigNumber(cleaned).decimalPlaces(token.decimals)
    : BigNumber(cleaned);

  if (capped) {
    return TokenAmount.fromWholeUnits(nearestValidAmount, token?.decimals).toCapped(
      undefined,
      true,
    );
  }

  return TokenAmount.fromWholeUnits(nearestValidAmount, token?.decimals).toFormatted(
    undefined,
    token?.decimals,
    'ROUND_DOWN',
    true,
  );
};

function useValidatedTokenAmountInput({
  token,
  initialValue,
}: {
  token?: Token | undefined;
  initialValue?: string;
}) {
  const [value, _setValue] = useState(formatValue(initialValue ?? '', false, token));

  const onInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = validateInputChange(event.target.value, token);
      if (newValue != null) {
        _setValue(newValue);
      }
    },
    [token, value],
  );

  const setValue = useCallback(
    (newValue: BigNumber.Value, capped?: boolean) => {
      _setValue(formatValue(newValue, capped, token));
    },
    [_setValue, token],
  );

  return { onChange: onInputChange, value, setValue };
}

export default useValidatedTokenAmountInput;
